import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../../data";
import { WebLayout } from "../../components/shell/WebLayout";
import { Coco } from "../../components/States";
import { fehlerText } from "../../lib/fehler";
import { currentLang, mitSprache, t } from "../../lib/sprache";

type Stand = "laedt" | "weiter" | "bestaetigt" | "abgelaufen" | "ungueltig" | "fehler" | "demo" | "abgemeldet";
type Rueckmeldung = "gesendet" | "warten" | "grenze" | null;

const ZIEL = "/einrichtung/registrierung";
const STAENDE: readonly Stand[] = ["laedt", "weiter", "bestaetigt", "abgelaufen", "ungueltig", "fehler", "abgemeldet"];

/**
 * Ziel des Ein-Klick-Links aus der Erinnerung an eine unbestätigte Registrierung (0243): /bestaetigen?t=<Schlüssel>. Die
 * Server-Funktion anmeldung-link bestätigt die Adresse und liefert einen einmaligen Anmeldelink; der führt angemeldet in die
 * Tarifwahl. Abgelaufen: „Neuen Link senden“ an dieselbe Adresse (nur mit dem alten Link, keine Eingabe). Demo-Vorschau der
 * Zustände mit ?vorschau=abgelaufen usw.
 */
export function AdresseBestaetigen() {
  const [params] = useSearchParams();
  const lang = currentLang();
  const token = (params.get("t") ?? "").trim().toLowerCase();
  // 0245: Abmeldelink der Erinnerung (…&abmelden=1): keine automatische Erinnerung mehr, ohne Bestätigen oder Anmelden
  const abmelden = params.get("abmelden") === "1";
  const vorschau = api.mode === "demo" ? (params.get("vorschau") as Stand | null) : null;
  const [stand, setStand] = useState<Stand>(vorschau && STAENDE.includes(vorschau) ? vorschau : "laedt");
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rueck, setRueck] = useState<Rueckmeldung>(null);

  useEffect(() => {
    if (vorschau) return;
    const einloesen = api.signupLink;
    if (!einloesen) { setStand("demo"); return; }
    if (!/^[0-9a-f]{64}$/.test(token)) { setStand("ungueltig"); return; }
    let alive = true;
    einloesen.call(api, abmelden ? "abmelden" : "einloesen", token).then((r) => {
      if (!alive) return;
      if (r.status === "ok" && r.link) { setStand("weiter"); window.location.replace(r.link); return; }
      setStand(r.status === "bestaetigt" || r.status === "abgelaufen" || r.status === "abgemeldet" ? r.status : "ungueltig");
    }).catch((e: unknown) => {
      if (!alive) return;
      setFehler(fehlerText(e));
      setStand("fehler");
    });
    return () => { alive = false; };
  }, [token, vorschau, abmelden]);

  async function neuerLink() {
    if (!api.signupLink) { setRueck("gesendet"); return; }
    setBusy(true);
    setFehler(null);
    try {
      const r = await api.signupLink("neu", token);
      if (r.status === "bestaetigt") setStand("bestaetigt");
      else if (r.status === "gesendet" || r.status === "warten" || r.status === "grenze") setRueck(r.status);
      else setStand("ungueltig");
    } catch (e) {
      setFehler(fehlerText(e));
    } finally {
      setBusy(false);
    }
  }

  const anmelden = mitSprache(`/anmelden?weiter=${encodeURIComponent(ZIEL)}`, lang);
  const kopf = (pose: "hinweis" | "erklaert", eyebrow: string, titel: string, text: React.ReactNode) => (
    <div className="tk-postfach-kopf">
      <Coco pose={pose} size={112} />
      <div className="stack">
        <p className="eyebrow">{eyebrow}</p>
        <h1 id="bestaetigen-h">{titel}</h1>
        <p>{text}</p>
      </div>
    </div>
  );

  let inhalt: React.ReactNode;
  if (stand === "laedt" || stand === "weiter") {
    inhalt = kopf("hinweis", t("Einen Moment", "One moment"),
      stand === "laedt" ? t("Wir bestätigen deine Adresse …", "Confirming your address …") : t("Adresse bestätigt", "Address confirmed"),
      stand === "laedt" ? t("Gleich geht es weiter.", "You will continue in a moment.") : t("Du wirst angemeldet und kommst direkt zur Tarifwahl.", "You are being signed in and go straight to choosing your plan."));
  } else if (stand === "abgelaufen") {
    inhalt = (
      <>
        {kopf("erklaert", t("Fast geschafft", "Almost done"), t("Dieser Link ist abgelaufen", "This link has expired"),
          t("Links aus der Erinnerung gelten 7 Tage. Wir schicken dir gern einen neuen an dieselbe Adresse.", "Links from the reminder are valid for 7 days. We are happy to send you a new one to the same address."))}
        {rueck === "gesendet" && <p className="notice" role="status">{t("Wir haben dir einen neuen Link geschickt. Schau in dein Postfach, auch im Spam- oder Werbung-Ordner.", "We have sent you a new link. Check your inbox, including the spam or promotions folder.")}</p>}
        {rueck === "warten" && <p className="notice" role="status">{t("Du hast gerade erst einen Link bekommen. Schau bitte in dein Postfach oder versuch es in 10 Minuten noch einmal.", "You only just received a link. Please check your inbox or try again in 10 minutes.")}</p>}
        {rueck === "grenze" && <p className="notice" role="status">{t("Wir haben dir schon mehrere Links geschickt. Melde dich bitte mit Adresse und Passwort an; dort kannst du dir einen neuen Code schicken lassen.", "We have already sent you several links. Please sign in with your email and password; there you can have a new code sent.")}</p>}
        {fehler && <p className="tk-error" role="alert">{fehler}</p>}
        <div className="row">
          {rueck !== "gesendet" && rueck !== "grenze" && (
            <button type="button" className="primary" disabled={busy} onClick={() => void neuerLink()}>{busy ? t("Wird gesendet …", "Sending …") : t("Neuen Link senden", "Send new link")}</button>
          )}
          <Link className="btn" to={anmelden}>{t("Mit Passwort anmelden", "Sign in with password")}</Link>
        </div>
      </>
    );
  } else if (stand === "abgemeldet") {
    inhalt = (
      <>
        {kopf("erklaert", t("Erledigt", "Done"), t("Keine Erinnerungen mehr", "No more reminders"),
          t("Wir schicken dir keine Erinnerungen zu deiner Registrierung mehr. Dein Konto bleibt angelegt; du kannst die Registrierung jederzeit abschließen.", "We will not send you any more reminders about your sign-up. Your account stays in place; you can complete the sign-up at any time."))}
        <div className="row">
          <Link className="btn primary" to={mitSprache(`/bestaetigen?t=${token}`, lang)}>{t("Registrierung jetzt abschließen", "Complete sign-up now")}</Link>
        </div>
      </>
    );
  } else if (stand === "bestaetigt") {
    inhalt = (
      <>
        {kopf("erklaert", t("Schön, dass du da bist", "Good to see you"), t("Deine Adresse ist schon bestätigt", "Your address is already confirmed"),
          t("Melde dich mit deiner Adresse und deinem Passwort an. Danach geht es mit der Tarifwahl weiter.", "Sign in with your email and password. After that you continue with choosing your plan."))}
        <div className="row"><Link className="btn primary" to={anmelden}>{t("Zur Anmeldung", "Go to sign in")}</Link></div>
      </>
    );
  } else if (stand === "demo") {
    inhalt = kopf("erklaert", t("Demo", "Demo"), t("Keine Bestätigungslinks in der Demo", "No confirmation links in the demo"),
      t("In der Demo gelten Konten sofort. Bestätigungslinks gibt es nur im echten Portal.", "In the demo, accounts are active right away. Confirmation links only exist in the real portal."));
  } else {
    inhalt = (
      <>
        {kopf("erklaert", t("Hoppla", "Oops"), stand === "fehler" ? t("Das hat gerade nicht geklappt", "That did not work just now") : t("Dieser Link funktioniert nicht", "This link does not work"),
          stand === "fehler" ? (fehler ?? t("Bitte gleich noch einmal versuchen.", "Please try again shortly."))
            : t("Vielleicht wurde er beim Kopieren gekürzt. Öffne ihn am besten direkt aus der Mail oder melde dich mit Adresse und Passwort an.", "It may have been shortened when copying. Best open it directly from the email or sign in with your email and password."))}
        <div className="row">
          {stand === "fehler" && <button type="button" className="primary" onClick={() => window.location.reload()}>{t("Noch einmal versuchen", "Try again")}</button>}
          <Link className={stand === "fehler" ? "btn" : "btn primary"} to={anmelden}>{t("Zur Anmeldung", "Go to sign in")}</Link>
          <Link className="btn" to={mitSprache("/registrieren", lang)}>{t("Kostenlos testen", "Try for free")}</Link>
        </div>
      </>
    );
  }

  return (
    <WebLayout lang={lang} title={t("Adresse bestätigen", "Confirm address")}>
      <main id="main" className="web">
        <div className="centerform">
          <section className="panel stack tk-postfach" aria-labelledby="bestaetigen-h" aria-busy={stand === "laedt" || stand === "weiter"}>
            {inhalt}
          </section>
        </div>
      </main>
    </WebLayout>
  );
}
